import React, { useState } from "react"
import { useMutation } from "urql"

import { InputWithLabel } from "../../../components/shared/Inputs"
import { ButtonLink } from "../../../components/shared/Links"
import { AnimatedModal } from "../../../components/shared/Modal"
import { useToast } from "../../../contexts/ToastContext"
import CalIcon from "../ProCalendarSettings/CalIcon"

import { CONNECT_APPLE_CALENDAR_MUTATION, CONNECT_ICAL_FEED_MUTATION } from "./graphql"
import { authorizeUrl, hasConnectionForProvider, isProviderConnectable, providerLabel } from "./selectors"
import { CalendarConnectionSummary, CalendarProvider, ConnectConnectionPayload } from "./types"

interface Props {
  connectableProviders: CalendarProvider[]
  connections: CalendarConnectionSummary[]
  onConnected: () => void
}

// Connect CTAs are rendered strictly from `connectableProviders` (backend
// configuration-gated). A provider missing from that list renders nothing —
// no fallback, no client-side inference. When a provider already has a
// connected account, the OAuth link forces the provider's account chooser so
// a second account can be added (otherwise Google/Microsoft silently reuse
// the active browser session).
const ConnectProviderButtons = ({ connectableProviders, connections, onConnected }: Props) => {
  const [appleModalVisible, setAppleModalVisible] = useState(false)
  const [feedModalVisible, setFeedModalVisible] = useState(false)
  const [appleId, setAppleId] = useState("")
  const [applePassword, setApplePassword] = useState("")
  const [feedUrl, setFeedUrl] = useState("")
  const [saving, setSaving] = useState(false)

  const [, connectAppleCalendar] = useMutation<{ connectAppleCalendar: ConnectConnectionPayload }>(
    CONNECT_APPLE_CALENDAR_MUTATION
  )
  const [, connectIcalFeed] = useMutation<{ connectIcalFeed: ConnectConnectionPayload }>(CONNECT_ICAL_FEED_MUTATION)
  const { showToast } = useToast()

  if (connectableProviders.length === 0) return null

  const saveApple = () => {
    setSaving(true)
    connectAppleCalendar({ appleId, appSpecificPassword: applePassword }).then((result) => {
      setSaving(false)
      const payload = result.data?.connectAppleCalendar
      if (payload?.result === "success") {
        showToast("Apple Calendar connected")
        setAppleModalVisible(false)
        setAppleId("")
        setApplePassword("")
        onConnected()
      } else {
        showToast({ content: payload?.errors?.[0] || "Could not connect Apple Calendar", type: "error" })
      }
    })
  }

  const saveFeed = () => {
    setSaving(true)
    connectIcalFeed({ feedUrl }).then((result) => {
      setSaving(false)
      const payload = result.data?.connectIcalFeed
      if (payload?.result === "success") {
        showToast("Calendar feed connected")
        setFeedModalVisible(false)
        setFeedUrl("")
        onConnected()
      } else {
        showToast({ content: payload?.errors?.[0] || "Could not connect calendar feed", type: "error" })
      }
    })
  }

  const googleConnected = hasConnectionForProvider(connections, "google")
  const microsoftConnected = hasConnectionForProvider(connections, "microsoft")
  const showAccountChooserHint =
    (googleConnected && isProviderConnectable("google", connectableProviders)) ||
    (microsoftConnected && isProviderConnectable("microsoft", connectableProviders))

  return (
    <div>
      <div className="flex flex-wrap gap-3">
        {isProviderConnectable("google", connectableProviders) && (
          <ButtonLink
            href={authorizeUrl("google", { selectAccount: googleConnected }) || "#"}
            variant="secondary"
            className="">
            <span className="flex items-center gap-2">
              <CalIcon provider="google" className="border-none" iconClassName="" />
              {googleConnected ? "Connect another Google account" : "Connect Google"}
            </span>
          </ButtonLink>
        )}
        {isProviderConnectable("microsoft", connectableProviders) && (
          <ButtonLink
            href={authorizeUrl("microsoft", { selectAccount: microsoftConnected }) || "#"}
            variant="secondary"
            className="">
            <span className="flex items-center gap-2">
              <CalIcon provider="live_connect" className="border-none" iconClassName="" />
              {microsoftConnected ? "Connect another Microsoft account" : "Connect Microsoft"}
            </span>
          </ButtonLink>
        )}
        {isProviderConnectable("apple", connectableProviders) && (
          <button
            className="rounded border border-gray bg-white px-4 font-bold hover:border-teal focus:outline-none focus:ring-2 focus:ring-blue"
            onClick={() => setAppleModalVisible(true)}>
            <span className="flex h-8 items-center gap-2">
              <CalIcon provider="apple" className="border-none" iconClassName="" />
              Connect Apple
            </span>
          </button>
        )}
        {isProviderConnectable("ical_feed", connectableProviders) && (
          <button
            className="rounded border border-gray bg-white px-4 font-bold hover:border-teal focus:outline-none focus:ring-2 focus:ring-blue"
            onClick={() => setFeedModalVisible(true)}>
            <span className="flex h-8 items-center gap-2">
              <CalIcon provider={undefined} className="border-none" iconClassName="" />
              Connect an iCal feed
            </span>
          </button>
        )}
      </div>

      {showAccountChooserHint && (
        <p className="mt-3 text-sm text-gray-dark">
          You&apos;ll see an account chooser on the next screen — pick &quot;Use another account&quot; and sign in to
          connect a calendar from a different account.
        </p>
      )}

      <AnimatedModal
        visible={appleModalVisible}
        hideModal={() => setAppleModalVisible(false)}
        header={`Connect ${providerLabel("apple")}`}
        showFooter={true}
        actionButtonCopy="Connect"
        saveDisabled={saving || !appleId || !applePassword}
        onSave={saveApple}>
        <p className="mb-4 text-sm text-gray-dark">
          Sign in with your Apple ID and an app-specific password. Create one at appleid.apple.com under Sign-In and
          Security, then paste it here. Your password is stored encrypted and never shown again.
        </p>
        <div className="flex flex-col gap-4">
          <InputWithLabel
            id="apple-id"
            className=""
            inputClassName=""
            labelClassName=""
            required={true}
            validationError={null}
            label="Apple ID"
            value={appleId}
            autoComplete="username"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAppleId(e.target.value)}
            placeholder="you@icloud.com"
          />
          <InputWithLabel
            id="apple-app-specific-password"
            className=""
            inputClassName=""
            labelClassName=""
            required={true}
            validationError={null}
            label="App-specific password"
            type="password"
            autoComplete="off"
            value={applePassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApplePassword(e.target.value)}
            placeholder="xxxx-xxxx-xxxx-xxxx"
          />
        </div>
      </AnimatedModal>

      <AnimatedModal
        visible={feedModalVisible}
        hideModal={() => setFeedModalVisible(false)}
        header="Connect an iCal feed"
        showFooter={true}
        actionButtonCopy="Connect"
        saveDisabled={saving || !feedUrl}
        onSave={saveFeed}>
        <p className="mb-4 text-sm text-gray-dark">
          Paste a secure (https) iCal feed URL. Feeds are read-only: they can block conflicting times but never receive
          Heal.me bookings.
        </p>
        <InputWithLabel
          id="ical-feed-url"
          className=""
          inputClassName=""
          labelClassName=""
          required={true}
          validationError={null}
          label="Feed URL"
          value={feedUrl}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFeedUrl(e.target.value)}
          placeholder="https://example.com/calendar.ics"
        />
      </AnimatedModal>
    </div>
  )
}

export default ConnectProviderButtons
